import React, { useState } from "react";
import { Box, Typography, Switch, Chip, Tooltip } from "@mui/material";
import { subscriptionService } from "../../api";
import { useAlert } from "../../context/AlertContext";

// Maps autoRenewStatus -> chip appearance.
const STATUS_CHIP = {
  active: { label: "Auto-renew on", color: "success" },
  disabled: { label: "Auto-renew off", color: "default" },
  past_due: { label: "Past due", color: "warning" },
  payment_method_required: { label: "Payment method required", color: "warning" },
};

// Renewal is cron-only — there is deliberately NO "renew now" / "retry" button.
// We only expose the autopay on/off toggle + a status chip.
const AutopayToggle = ({
  subscriptionId,
  initialAutoRenew = false,
  autoRenewStatus = "",
  hasPaymentAuthorization = false,
}) => {
  const { showAlert } = useAlert();
  const [enabled, setEnabled] = useState(Boolean(initialAutoRenew));
  const [status, setStatus] = useState(autoRenewStatus);
  const [busy, setBusy] = useState(false);

  const chip = STATUS_CHIP[status] || {
    label: enabled ? "Auto-renew on" : "Auto-renew off",
    color: enabled ? "success" : "default",
  };

  // Can't enable autopay without a reusable payment authorization on file.
  const enableBlocked = !enabled && !hasPaymentAuthorization;

  const handleToggle = async (event) => {
    const next = event.target.checked;
    setBusy(true);
    try {
      // Contract: { autoRenew: boolean, cancelAtPeriodEnd? }. subscriptionId is
      // SuperAdmin-only and a no-op for a regular admin, so we don't send it.
      const res = await subscriptionService.autopay({ autoRenew: next });
      if (res?.success && res?.data) {
        setEnabled(Boolean(res.data.autoRenew));
        setStatus(res.data.autoRenewStatus || "");
        showAlert(res?.message || "Auto-renew updated.", "success");
      } else {
        // e.g. "No reusable payment authorization found. Complete one
        // successful card payment first."
        showAlert(res?.message || "Could not update auto-renew.", "error");
      }
    } catch (err) {
      showAlert(err?.message || "Could not update auto-renew.", "error");
    } finally {
      setBusy(false);
    }
  };

  if (!subscriptionId) return null;

  const toggle = (
    <Switch
      checked={enabled}
      onChange={handleToggle}
      disabled={busy || enableBlocked}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        p: 2,
        mb: 2,
        borderRadius: "12px",
        border: "1px solid #ececec",
        backgroundColor: "#fafafa",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#202020" }}>
          Auto-renew
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#878787" }}>
          {enableBlocked
            ? "Complete one successful card payment to enable auto-renew."
            : "Automatically renew your subscription each billing cycle."}
        </Typography>
      </Box>
      <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />
      {enableBlocked ? (
        <Tooltip title="No reusable payment authorization on file yet">
          <span>{toggle}</span>
        </Tooltip>
      ) : (
        toggle
      )}
    </Box>
  );
};

export default AutopayToggle;
